import serverCall from "../../serverCall";
import { getGlobalSocket, initGlobalSocket } from "../socket";
import { deductTokens, setTokenStatus } from "../../store/slices/userSlice";

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") {
        const commaIdx = result.indexOf(",");
        resolve(commaIdx !== -1 ? result.substring(commaIdx + 1) : result);
      } else {
        resolve("");
      }
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
};

const translateAudioViaSocket = (file, targetLanguage, onChunk, dispatch) => {
  return new Promise(async (resolve, reject) => {
    let sock = getGlobalSocket();
    if (!sock || !sock.connected) {
      sock = initGlobalSocket(dispatch);
    }
    if (!sock) {
      console.warn("[Global Socket] Socket unavailable for translateAudioViaSocket, falling back to HTTP stream");
      return translateAudioStream(file, targetLanguage, onChunk).then(resolve).catch(reject);
    }

    let base64Audio = "";
    try {
      base64Audio = await fileToBase64(file);
    } catch (e) {
      return reject(e);
    }

    let timeoutTimer = null;
    let resolved = false;

    let accumulatedSegments = [];
    let accumulatedTranscription = "";
    let accumulatedTranslation = "";
    let lastPi = null;

    const cleanup = () => {
      if (timeoutTimer) clearTimeout(timeoutTimer);
      sock.off("uploadaudio-response", handleResponse);
      sock.off("uploadAudio-response", handleResponse);
      sock.off("uploadaudio-result", handleResponse);
      sock.off("upload-audio-response", handleResponse);
      sock.off("uploadaudio-progress", handleResponse);
      sock.off("uploadAudio-progress", handleResponse);
      sock.off("uploadaudio", handleSameEvent);
      sock.off("uploadAudio", handleSameEvent);
      sock.off("translation-result", handleResponse);
      sock.off("translation-error", handleError);
      sock.off("uploadaudio-error", handleError);
      sock.off("uploadAudio-error", handleError);
      sock.off("upload-audio-error", handleError);
      sock.off("translate-audio-error", handleError);
      sock.off("error", handleError);
      sock.off("connect_error", handleConnectError);
    };

    const handleResult = (data) => {
      if (resolved) return;
      resolved = true;
      cleanup();

      const d = data?.data || data?.latestAudioTranslation || data;
      const transcription = d?.transcription || d?.originalText || d?.extractedText || data?.transcription || data?.originalText || accumulatedTranscription || "";
      const translation = d?.translation || d?.translatedText || data?.translation || data?.translatedText || accumulatedTranslation || "";

      let segments = [];
      if (Array.isArray(d?.segments) && d.segments.length > 0) {
        segments = d.segments;
      } else if (Array.isArray(d?.chunks)) {
        d.chunks.forEach((chunk) => {
          if (Array.isArray(chunk?.segments)) {
            segments.push(...chunk.segments);
          }
        });
      } else if (Array.isArray(data?.segments) && data.segments.length > 0) {
        segments = data.segments;
      } else if (accumulatedSegments.length > 0) {
        segments = accumulatedSegments;
      }

      const audioUrl = d?.audioFileName || d?.audioUrl || d?.audioFile || data?.audioFileName || data?.audioUrl || null;
      const pi = d?.personalIdentifiersProtected ?? (typeof data?.personalIdentifiersProtected === "number" ? data.personalIdentifiersProtected : lastPi);
      const id = d?.id || d?._id || data?.id || data?._id || null;
      const percentage = d?.percentage ?? (typeof data?.percentage === "number" ? data.percentage : 100);

      if (onChunk) {
        if (transcription) onChunk({ type: "transcription", transcription, percentage, personalIdentifiersProtected: pi });
        if (segments.length > 0) {
          segments.forEach((seg) => onChunk({ type: "chunk", data: seg, percentage, personalIdentifiersProtected: pi }));
        }
        onChunk({ type: "done", translation, transcription, percentage: 100, personalIdentifiersProtected: pi });
      }

      try {
        localStorage.removeItem("linko_pending_transcribe");
        localStorage.removeItem("linko_transcribe_progress");
      } catch (_) {}
      try {
        window.dispatchEvent(
          new CustomEvent("transcriptionCompleted", { detail: data })
        );
      } catch (_) {}

      resolve({
        transcription,
        translation,
        id,
        percentage: 100,
        personalIdentifiersProtected: pi,
        segments,
        audioFileName: audioUrl,
        audioUrl,
      });
    };

    const handleError = (err) => {
      if (resolved) return;
      resolved = true;
      cleanup();
      const rawData = err?.latestAudioTranslation || err?.data || err;
      const errMsg = err?.message || err?.error || rawData?.message || rawData?.error || (typeof err === "string" ? err : "Audio transcription failed");
      const errorObj = new Error(errMsg);

      let errSegments = [];
      if (Array.isArray(rawData?.segments) && rawData.segments.length > 0) {
        errSegments = rawData.segments;
      } else if (accumulatedSegments.length > 0) {
        errSegments = accumulatedSegments;
      }

      errorObj.partialResult = {
        transcription: accumulatedTranscription || rawData?.transcription || rawData?.originalText || "",
        translation: accumulatedTranslation || rawData?.translation || rawData?.translatedText || "",
        segments: errSegments,
        personalIdentifiersProtected: rawData?.personalIdentifiersProtected ?? lastPi,
        id: rawData?.id || rawData?._id || null,
        audioFileName: rawData?.audioFileName || rawData?.audioUrl || null,
      };

      try {
        localStorage.removeItem("linko_pending_transcribe");
        localStorage.removeItem("linko_transcribe_progress");
      } catch (_) {}
      try {
        window.dispatchEvent(
          new CustomEvent("transcriptionError", {
            detail: { error: errMsg, partialResult: errorObj.partialResult },
          })
        );
      } catch (_) {}

      reject(errorObj);
    };

    const handleConnectError = (err) => {
      console.warn("[Global Socket] Socket connect_error during translateAudioViaSocket, falling back:", err);
      if (!resolved) {
        resolved = true;
        cleanup();
        translateAudioStream(file, targetLanguage, onChunk).then(resolve).catch(reject);
      }
    };

    const handleResponse = (raw) => {
      if (!raw || resolved) return;

      const d = raw?.data || raw;

      // 1. Token deduction & status updates
      const tokenCost = Number(d?.totalToken ?? raw?.totalToken ?? (Number(d?.inputToken || raw?.inputToken || 0) + Number(d?.outputToken || raw?.outputToken || 0))) || 0;
      if (tokenCost > 0 && dispatch) {
        dispatch(deductTokens(tokenCost));
      }
      if (d?.available_tokens !== undefined || d?.availableTokens !== undefined || raw?.available_tokens !== undefined || raw?.availableTokens !== undefined) {
        dispatch(setTokenStatus(d?.available_tokens !== undefined || d?.availableTokens !== undefined ? d : raw));
      }

      // Track any partial text/segments
      const piVal = d?.personalIdentifiersProtected ?? raw?.personalIdentifiersProtected ?? null;
      if (typeof piVal === "number") lastPi = piVal;

      if (Array.isArray(d?.segments) && d.segments.length > 0) {
        accumulatedSegments = d.segments;
      } else if (Array.isArray(raw?.segments) && raw.segments.length > 0) {
        accumulatedSegments = raw.segments;
      } else if (d?.segment || raw?.segment) {
        const singleSeg = d?.segment || raw?.segment;
        if (!accumulatedSegments.some((s) => s.start === singleSeg.start && s.end === singleSeg.end)) {
          accumulatedSegments.push(singleSeg);
        }
      }

      if (d?.transcription || raw?.transcription) {
        accumulatedTranscription = d?.transcription || raw?.transcription;
      }
      if (d?.translation || d?.translatedText || raw?.translation || raw?.translatedText) {
        accumulatedTranslation = d?.translation || d?.translatedText || raw?.translation || raw?.translatedText;
      }

      // 2. Error handling
      if (
        raw?.status === "error" ||
        raw?.status === "failed" ||
        raw?.status === false ||
        raw?.responseType === "error" ||
        (typeof raw?.statusCode === "number" && raw.statusCode >= 400) ||
        d?.status === "failed" ||
        d?.status === "error" ||
        d?.error
      ) {
        handleError(raw);
        return;
      }

      // 3. Progress updates & Completion check
      const isComplete =
        raw?.status === "completed" ||
        d?.status === "completed" ||
        raw?.responseType === "done" ||
        d?.responseType === "done";

      const percentageVal = d?.percentage ?? raw?.percentage ?? d?.progress ?? raw?.progress ?? null;
      if (percentageVal !== null && percentageVal !== undefined) {
        const p = isComplete ? 100 : Math.min(99, Math.max(0, Number(percentageVal)));
        try {
          if (isComplete) {
            localStorage.removeItem("linko_transcribe_progress");
          } else {
            localStorage.setItem("linko_transcribe_progress", String(p));
          }
          window.dispatchEvent(
            new CustomEvent("transcribeProgressUpdated", { detail: p })
          );
        } catch (_) {}
        if (onChunk) {
          onChunk({ type: isComplete ? "done" : "progress", percentage: p, personalIdentifiersProtected: lastPi });
        }
      }

      // If not yet completed (still in processing / segment streaming), do not resolve or render UI yet
      if (!isComplete) {
        return;
      }

      // 4. Completed: render and resolve final result
      handleResult(raw);
    };

    const handleSameEvent = (payload) => {
      const d = payload?.data || payload;
      const isComplete =
        payload?.status === "completed" ||
        d?.status === "completed" ||
        payload?.responseType === "done" ||
        d?.responseType === "done";
      if (isComplete) {
        handleResult(payload);
      }
    };

    sock.on("uploadaudio-response", handleResponse);
    sock.on("uploadAudio-response", handleResponse);
    sock.on("uploadaudio-result", handleResponse);
    sock.on("upload-audio-response", handleResponse);
    sock.on("uploadaudio-progress", handleResponse);
    sock.on("uploadAudio-progress", handleResponse);
    sock.on("uploadaudio", handleSameEvent);
    sock.on("uploadAudio", handleSameEvent);
    sock.on("translation-result", handleResponse);
    sock.on("translation-error", handleError);
    sock.on("uploadaudio-error", handleError);
    sock.on("uploadAudio-error", handleError);
    sock.on("upload-audio-error", handleError);
    sock.on("translate-audio-error", handleError);
    sock.on("error", handleError);
    sock.on("connect_error", handleConnectError);

    const emitUpload = () => {
      // console.log("[Global Socket] Emitting 'uploadaudio' event for file:", file.name, "size:", file.size, "targetLanguage:", targetLanguage);
      sock.emit("uploadaudio", {
        audioFile: base64Audio,
        fileName: file.name,
        targetLanguage: targetLanguage,
        language: targetLanguage,
      }, (ackResponse) => {
        if (ackResponse) {
          // console.log("[Global Socket] 'uploadaudio' ack received:", ackResponse);
          handleResponse(ackResponse);
        }
      });
    };

    if (sock.connected) {
      emitUpload();
    } else {
      sock.once("connect", emitUpload);
    }

    // Dynamic timeout based on file size (minimum 5 mins, up to 30 mins for huge files)
    const timeoutDuration = Math.max(300000, Math.min(1800000, ((file.size || 0) / (1024 * 1024)) * 30000 || 600000));
    timeoutTimer = setTimeout(() => {
      if (!resolved) {
        console.warn("[Global Socket] Socket 'uploadaudio' timeout, falling back to HTTP stream...");
        cleanup();
        translateAudioStream(file, targetLanguage, onChunk).then(resolve).catch(reject);
      }
    }, timeoutDuration);
  });
};

const translateAudio = (file, targetLanguage) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("targetLanguage", targetLanguage);
    const response = serverCall.post(`/ai-conversation/translate-audio`, formData);
    return response;
  } catch (error) {
    throw error;
  }
};

const translateAudioStream = (file, targetLanguage, onChunk) => {
  return new Promise(async (resolve, reject) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("targetLanguage", targetLanguage);

      const token = localStorage.getItem("linko_web");
      const baseURL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, "") || "";

      const response = await fetch(`${baseURL}/ai-conversation/translate-audio`, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const partialData = errData?.latestAudioTranslation || errData?.data || errData;
        const err = new Error(errData?.message || errData?.error || `Request failed: ${response.status}`);
        err.partialResult = partialData;
        return reject(err);
      }

      const contentType = response.headers.get("content-type") || "";

      // If content-type is explicitly JSON, handle as plain JSON response
      if (contentType.includes("application/json")) {
        const data = await response.json();
        const d = data?.latestAudioTranslation || data?.data || data;
        const percentage = d?.percentage ?? data?.percentage ?? null;
        if (onChunk && percentage !== null && percentage !== undefined) {
          onChunk({ type: "progress", percentage: Number(percentage) });
        }

        const hasPartial = !!(
          d?.transcription ||
          d?.translation ||
          d?.translatedText ||
          (Array.isArray(d?.segments) && d.segments.length > 0)
        );
        const errorMsg = data?.error || d?.error || (data?.status === false && data?.message) || (data?.success === false && data?.message) || (d?.status === "failed" && (d?.error || d?.message)) || null;

        if (errorMsg || data?.status === "failed" || data?.status === false || d?.status === "failed") {
          if (hasPartial) {
            return resolve({
              transcription: d?.transcription || "",
              translation: d?.translation || d?.translatedText || "",
              id: d?.id || d?._id || null,
              percentage: percentage !== null ? Number(percentage) : 100,
              personalIdentifiersProtected: d?.personalIdentifiersProtected ?? (typeof data?.personalIdentifiersProtected === "number" ? data.personalIdentifiersProtected : null),
              segments: Array.isArray(d?.segments) ? d.segments : [],
              audioFileName: d?.audioFileName || d?.audioUrl || d?.audioFile || null,
              audioUrl: d?.audioUrl || d?.audioFileName || d?.audioFile || null,
              error: errorMsg || "Audio translation incomplete",
              status: d?.status || data?.status || "failed",
            });
          }
          const err = new Error(errorMsg || "Audio transcription failed");
          err.partialResult = d;
          return reject(err);
        }

        return resolve({
          transcription: d?.transcription || "",
          translation: d?.translation || d?.translatedText || "",
          id: d?.id || d?._id || null,
          percentage: percentage !== null ? Number(percentage) : 100,
          personalIdentifiersProtected: d?.personalIdentifiersProtected ?? (typeof data?.personalIdentifiersProtected === "number" ? data.personalIdentifiersProtected : null),
          segments: Array.isArray(d?.segments) ? d.segments : [],
          audioFileName: d?.audioFileName || d?.audioUrl || d?.audioFile || null,
          audioUrl: d?.audioUrl || d?.audioFileName || d?.audioFile || null,
        });
      }

      // SSE stream parsing — works regardless of content-type header
      // (production proxies may strip text/event-stream, so we always try stream first)
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let currentEvent = "";
      let result = { transcription: "", translation: "", id: null, personalIdentifiersProtected: null, percentage: 0, segments: [] };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith("event:")) {
            currentEvent = line.replace("event:", "").trim();
          } else if (line.startsWith("data:")) {
            const raw = line.replace("data:", "").trim();
            try {
              const parsed = JSON.parse(raw);
              const isError = currentEvent === "error" || parsed?.error || (parsed?.status === false && parsed?.message) || parsed?.status === "failed";
              if (isError) {
                const errMsg = parsed?.message || parsed?.error || "Audio transcription failed";
                const d = parsed?.latestAudioTranslation || parsed?.data || parsed;
                const partialTranscription = d?.transcription || result.transcription;
                const partialTranslation = d?.translation || d?.translatedText || result.translation;
                const partialSegments = (Array.isArray(d?.segments) && d.segments.length > 0) ? d.segments : (result.segments.length > 0 ? result.segments : []);
                const partialAudio = d?.audioFileName || d?.audioUrl || d?.audioFile || result.audioUrl || null;
                const pi = d?.personalIdentifiersProtected ?? result.personalIdentifiersProtected ?? null;

                if (partialTranscription || partialTranslation || partialSegments.length > 0) {
                  return resolve({
                    transcription: partialTranscription,
                    translation: partialTranslation,
                    id: d?.id || d?._id || result.id,
                    percentage: result.percentage || d?.percentage || 0,
                    personalIdentifiersProtected: pi,
                    segments: partialSegments,
                    audioFileName: partialAudio,
                    audioUrl: partialAudio,
                    error: errMsg,
                    status: "failed",
                  });
                }
                const err = new Error(errMsg);
                err.partialResult = {
                  transcription: result.transcription,
                  translation: result.translation,
                  segments: result.segments,
                  percentage: result.percentage,
                };
                return reject(err);
              }
              if (parsed?.id || parsed?._id) {
                result.id = parsed.id || parsed._id;
              }
              if (parsed?.audioFileName || parsed?.audioUrl || parsed?.audioFile) {
                result.audioFileName = parsed.audioFileName || parsed.audioUrl || parsed.audioFile;
                result.audioUrl = parsed.audioUrl || parsed.audioFileName || parsed.audioFile;
              }
              if (parsed?.personalIdentifiersProtected !== undefined && parsed?.personalIdentifiersProtected !== null) {
                result.personalIdentifiersProtected = parsed.personalIdentifiersProtected;
              }
              const percentageVal = parsed?.percentage ?? parsed?.data?.percentage ?? parsed?.progress ?? null;
              if (percentageVal !== null && percentageVal !== undefined) {
                result.percentage = Number(percentageVal);
                if (onChunk) onChunk({ type: "progress", percentage: result.percentage, data: parsed, personalIdentifiersProtected: result.personalIdentifiersProtected });
              }
              if (currentEvent === "transcription") {
                result.transcription = parsed?.transcription || "";
                if (onChunk) onChunk({ type: "transcription", transcription: result.transcription, percentage: result.percentage, personalIdentifiersProtected: result.personalIdentifiersProtected });
              } else if (currentEvent === "chunk") {
                if (parsed?.translatedText) {
                  result.translation = (result.translation ? result.translation + " " : "") + parsed.translatedText;
                }
                if (parsed?.originalText) {
                  result.transcription = (result.transcription ? result.transcription + " " : "") + parsed.originalText;
                } else if (parsed?.transcription && !result.transcription) {
                  result.transcription = parsed.transcription;
                }
                result.segments.push(parsed);
                if (onChunk) onChunk({ type: "chunk", data: parsed, percentage: result.percentage, personalIdentifiersProtected: result.personalIdentifiersProtected });
              } else if (currentEvent === "done") {
                result.transcription = parsed?.transcription || result.transcription;
                result.translation = parsed?.translation || parsed?.translatedText || result.translation;
                result.id = parsed?.id || parsed?._id || result.id || null;
                if (Array.isArray(parsed?.segments)) {
                  result.segments = parsed.segments;
                }
                if (parsed?.audioFileName || parsed?.audioUrl) {
                  result.audioFileName = parsed.audioFileName || parsed.audioUrl;
                  result.audioUrl = parsed.audioUrl || parsed.audioFileName;
                }
                if (onChunk) onChunk({ type: "done", translation: result.translation, transcription: result.transcription, percentage: 100, personalIdentifiersProtected: result.personalIdentifiersProtected });
              } else if (currentEvent === "progress") {
                if (onChunk) onChunk({ type: "progress", percentage: result.percentage, data: parsed, personalIdentifiersProtected: result.personalIdentifiersProtected });
              }
            } catch { }
          }
        }
      }

      resolve(result);
    } catch (err) {
      reject(err);
    }
  });
};

const retranslate = (id, transcription, targetLanguage, onChunk) => {
  return new Promise(async (resolve, reject) => {
    try {
      const token = localStorage.getItem("linko_web");
      const baseURL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, "") || "";

      const response = await fetch(`${baseURL}/ai-conversation/audio-translation/${id}/retranslate`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ transcription, targetLanguage }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        return reject(new Error(errData?.message || `Request failed: ${response.status}`));
      }

      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        const data = await response.json();
        if (data?.status === false || data?.error || (data?.success === false && data?.message)) {
          return reject(new Error(data?.message || data?.error || "Audio re-translation failed"));
        }
        const d = data?.data || data;
        return resolve({ data: { data: { translation: d?.translation || d?.translatedText || "", transcription: d?.transcription || "" } } });
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let currentEvent = "";
      let result = { translation: "", transcription: "" };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith("event:")) {
            currentEvent = line.replace("event:", "").trim();
          } else if (line.startsWith("data:")) {
            const raw = line.replace("data:", "").trim();
            try {
              const parsed = JSON.parse(raw);
              if (currentEvent === "error" || parsed?.error || (parsed?.status === false && parsed?.message)) {
                return reject(new Error(parsed?.message || parsed?.error || "Audio re-translation failed"));
              }
              if (currentEvent === "chunk") {
                if (parsed?.translatedText) result.translation = parsed.translatedText;
                if (onChunk) onChunk({ type: "chunk", data: parsed });
              } else if (currentEvent === "done") {
                result.translation = parsed?.translation || parsed?.translatedText || result.translation;
                result.transcription = parsed?.transcription || result.transcription;
              }
            } catch { }
          }
        }
      }

      resolve({ data: { data: { translation: result.translation, transcription: result.transcription } } });
    } catch (err) {
      reject(err);
    }
  });
};

const uploadAudioBySession = (sessionId, file, fileName) => {
  return new Promise(async (resolve, reject) => {
    try {
      const formData = new FormData();
      formData.append("sessionId", sessionId);
      if (fileName) {
        formData.append("file", file, fileName);
      } else {
        formData.append("file", file);
      }

      const token = localStorage.getItem("linko_web");
      const baseURL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, "") || "";

      const response = await fetch(`${baseURL}/ai-conversation/audio-upload-by-session`, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        return reject(new Error(errData?.message || `Request failed: ${response.status}`));
      }

      const data = await response.json();
      resolve(data);
    } catch (err) {
      reject(err);
    }
  });
};

const TranscribeService = {
  translateAudio,
  translateAudioStream,
  translateAudioViaSocket,
  uploadAudioBySession,
  retranslate,
};

export default TranscribeService;
